import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { PlanInterval } from '../../../../models';

const useStyles = makeStyles(theme => ({
  disabledWrap: {
    display: 'inline-flex',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: 98,
    height: 36,
    padding: theme.spacing(0, 1.5),
    borderRadius: 20,
    border: '1px solid #C7C9D1',
    backgroundColor: '#ffffff',
    fontSize: 14,
    fontWeight: 500,
    color: '#22283C',
    transition: theme.transitions.create(['border-color'], {
      duration: 150,
    }),
    '&:hover': {
      borderColor: '#A9ACB7',
    },
  },
  triggerDisabled: {
    color: '#808B9A',
    backgroundColor: '#ffffff',
    borderColor: '#E5E7EB',
    cursor: 'default',
    '&:hover': {
      borderColor: '#E5E7EB',
    },
  },
  triggerLabel: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1,
  },
  chevron: {
    fontSize: 18,
    color: '#6B7280',
  },
  paper: {
    marginTop: theme.spacing(0.5),
    minWidth: 120,
    borderRadius: 12,
    boxShadow: '0 6px 24px 0 rgba(34, 40, 60, 0.16)',
  },
  menuList: {
    padding: theme.spacing(0.5),
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    borderRadius: 8,
    fontFamily: 'Inter, sans-serif',
    fontSize: 13.5,
    fontWeight: 500,
    color: '#22283C',
    padding: theme.spacing(1, 1.5),
    '&:hover, &.Mui-selected:hover': {
      backgroundColor: 'transparent',
    },
    '&.Mui-selected': {
      backgroundColor: 'transparent',
    },
    '&:hover $menuItemLabel': {
      textDecoration: 'underline',
      textDecorationColor: '#22283C',
    },
  },
  menuItemLabel: {
    color: '#22283C',
    textDecorationColor: '#22283C',
  },
  checkIcon: {
    fontSize: 18,
    color: '#12B76A',
  },
}));

const INTERVAL_OPTIONS: PlanInterval[] = [PlanInterval.YEAR, PlanInterval.MONTH];

export const PlanBillingSwitch = (
  { interval, onChange, disabled }: {
    interval: PlanInterval;
    onChange: (interval: PlanInterval) => void;
    disabled?: boolean;
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const labelFor = (value: PlanInterval) => intl.formatMessage({
    id: value === PlanInterval.YEAR
      ? 'page.plan.v2.billing.annual'
      : 'page.plan.v2.billing.monthly',
  });

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (value: PlanInterval) => {
    if (value !== interval) {
      onChange(value);
    }
    handleClose();
  };

  const trigger = (
    <ButtonBase
      className={clsx(classes.trigger, { [classes.triggerDisabled]: disabled })}
      onClick={handleOpen}
      disabled={disabled}
      disableRipple
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={intl.formatMessage({ id: 'page.plan.v2.billing.cycle' })}
    >
      <Typography component="span" className={classes.triggerLabel}>
        {labelFor(interval)}
      </Typography>
      {open
        ? <KeyboardArrowUpIcon className={classes.chevron} />
        : <KeyboardArrowDownIcon className={classes.chevron} />}
    </ButtonBase>
  );

  return (
    <>
      {disabled ? (
        <Tooltip
          arrow
          title={intl.formatMessage({ id: 'page.plan.v2.billing.disabled.tooltip' })}
        >
          <Box component="span" className={classes.disabledWrap}>{trigger}</Box>
        </Tooltip>
      ) : trigger}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classes.paper, list: classes.menuList }}
        MenuListProps={{ role: 'listbox' }}
      >
        {INTERVAL_OPTIONS.map(value => {
          const selected = value === interval;
          return (
            <MenuItem
              key={value}
              selected={selected}
              onClick={() => handleSelect(value)}
              className={classes.menuItem}
              role="option"
              aria-selected={selected}
            >
              <Box component="span" className={classes.menuItemLabel}>{labelFor(value)}</Box>
              {selected && <CheckIcon className={classes.checkIcon} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
